import type { SprintCarryoverReason, SprintStatus } from "../../shared/types/api";
import type { Tone } from "../../shared/ui";

/** How sprint vocabulary reads, in one place so every screen agrees. */

export const SPRINT_STATUS_LABELS: Record<SprintStatus, string> = {
  PLANNED: "Planned",
  ACTIVE: "Active",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
};

export const SPRINT_STATUS_TONE: Record<SprintStatus, Tone> = {
  PLANNED: "gray",
  ACTIVE: "green",
  COMPLETED: "blue",
  CANCELLED: "gray",
};

/**
 * Story points, which are counts and never hours (§53).
 *
 * <p>Spelled out rather than abbreviated to "pts" on figures that stand alone, because the whole
 * point of §53 is that somebody reading a number should never be in doubt about which unit it is.
 */
export function formatPoints(points: number): string {
  return `${points} ${points === 1 ? "point" : "points"}`;
}

export function formatHours(hours: number | null | undefined): string {
  if (hours == null) {
    return "—";
  }
  const rounded = Math.round(hours * 10) / 10;
  return `${rounded.toLocaleString(undefined, { maximumFractionDigits: 1 })}h`;
}

export function formatPercent(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}

/** How much of a sprint is left, phrased the way a team says it out loud. */
export function formatDaysRemaining(days: number, status: SprintStatus): string {
  if (status !== "ACTIVE") {
    return "—";
  }
  if (days === 0) {
    return "last day";
  }
  return `${days} ${days === 1 ? "day" : "days"} left`;
}

/**
 * What a capacity figure means when it is over-committed.
 *
 * <p>Deliberately not a red badge on its own. Committing slightly over capacity is normal and
 * often right; committing at double is a conversation. The wording scales with the number so the
 * screen does not cry wolf at 105%.
 */
export function describeUtilisation(fraction: number): {
  tone: Tone;
  label: string;
} {
  if (fraction === 0) {
    return { tone: "gray", label: "Nothing committed yet" };
  }
  if (fraction <= 0.85) {
    return { tone: "green", label: "Room to spare" };
  }
  if (fraction <= 1.05) {
    return { tone: "green", label: "About right" };
  }
  if (fraction <= 1.25) {
    return { tone: "amber", label: "Slightly over capacity" };
  }
  return { tone: "red", label: "Well over capacity" };
}

/**
 * Why work did not land, in the order somebody closing a sprint would consider it.
 *
 * <p>Ordered rather than alphabetical: the first two are about the plan, the next four about
 * things that got in the way, and the last two about people and priorities. Somebody working
 * down a list of eight items reaches for the same shape of answer twice in a row, and an order
 * that groups them makes that a shorter read.
 */
export const CARRYOVER_REASONS: { value: SprintCarryoverReason; label: string }[] = [
  { value: "NOT_STARTED", label: "Not started" },
  { value: "UNDERESTIMATED", label: "Bigger than estimated" },
  { value: "BLOCKED_BY_DEPENDENCY", label: "Blocked by a dependency" },
  { value: "BLOCKED_BY_DEFECT", label: "Blocked by a defect" },
  { value: "AWAITING_REVIEW", label: "Waiting on review" },
  { value: "AWAITING_QA", label: "Waiting on QA" },
  { value: "PEOPLE_UNAVAILABLE", label: "People were unavailable" },
  { value: "PRIORITY_CHANGED", label: "Priority changed mid-sprint" },
  { value: "OTHER", label: "Other" },
];

/** Whether the written explanation is required alongside the code. */
export function carryoverNeedsComment(reason: SprintCarryoverReason): boolean {
  return reason === "OTHER";
}

/**
 * What a sprint's closing summary says about what it did not finish.
 *
 * <p>Plain counting, and deliberately not a judgement. A sprint that carries two of nine items
 * is ordinary; the screen's job is to make sure nobody closes without seeing the number.
 */
export function describeCarryover(unfinished: number, total: number): string {
  if (unfinished === 0) {
    return total === 0
      ? "Nothing in this sprint."
      : "Everything in this sprint is finished. Nothing to carry.";
  }
  return `${unfinished} of ${total} ${
    total === 1 ? "item" : "items"
  } did not finish. Each one needs a reason before the sprint can close.`;
}
